import React from 'react'

import { useAppContext } from '@hooks/use-app-context.ts'
import { useEffect, useState } from 'react'
import { useApi } from '@hooks/use-api-requests.hook.ts'
import { Actions } from '@store/reducer.ts'
import { useModal } from '@hooks/use-modal.ts'

import { getFormattedDate } from '@utils/dates'
import {
	currentStepAtom,
	documentFlowVariantAtom,
	modalShowHeader,
	qrMissingDocsCompletedAtom,
	selectedSubStepAtom,
} from '@store/atoms'
import { useAtom, useSetAtom } from 'jotai'
import { calculateCAP } from '@utils/validation-net'
import { StepsRichiesta } from '@store/stepsRichiesta'

import.meta.env.BASE_URL
const POLLING_INTERVAL = 10 // seconds

type UserData = {
	Nome: string
	Cognome: string
	DataDiNascita: string
	Genere: string
	CodiceFiscale: string
	LuogoDiNascita: string
	PaeseDiNascita: string
	Cittadinanza: string
	NumeroDocumento: string
	ProvinciaRilascio: string
	ComuneRilascio: string
	TipoDocumento: string
	DataEmissione: string
	CapRilascio: string
}

function useQR() {
	const [flowVariant, setDocumentFlowVariant] = useAtom(documentFlowVariantAtom) 
	const [activeStep, setActiveStep] = useAtom(currentStepAtom) //STEP PADRE -contact, dati anagrafici
	const { state, dispatch } = useAppContext()
	const setQrMissingDocsCompleted = useSetAtom(qrMissingDocsCompletedAtom)
	const { close } = useModal()

	const [intervalID, setIntervalID] = useState<number | null>(null)
	const [useOnMobile, setMobile] = useState(false)
	const setShowModalHeader = useSetAtom(modalShowHeader)

	const setSelectedSubStep = useSetAtom(selectedSubStepAtom)

	const [mobileToken, fetchMobileToken, loadingToken] = useApi<string>({
		request: 'DbOperations/initMobileSession',
		method: 'POST',
		shouldCache: false,
		sleeper: true,
	})

	const [userData, fetchUserData] = useApi<UserData>({
		request: 'DbOperations/GetInfoCertData',
		method: 'POST',
		sleeper: true,
		silent: true,
		shouldCache: false,
	})

	const [session, fetchSession] = useApi<{
		idRichiesta: number
		source: 'MOBILE' | 'DESKTOP'
		status: 'PENDING' | 'ACTIVE' | 'CLOSED' | 'EXPIRED' | 'REVIEW'
	}>({
		request: 'DbOperations/GetBrowserState',
		method: 'GET',
		silent: true,
		sleeper: true,
		shouldCache: false,
	})

	const pendingReviewRef = React.useRef(false)
	// Tracks whether we have seen at least one non-terminal status (PENDING/ACTIVE)
	// since this hook mounted. Terminal statuses (REVIEW/CLOSED) seen on the very
	// first poll in a missing-docs flow are stale leftovers from the previous upload
	// cycle and must be ignored — the session is intentionally reused across uploads.
	const hasSeenActiveRef = React.useRef(false)

	console.log('SESSION STATE ', session, activeStep)
	useEffect(() => {

		const hasSpecialDocumentFlow =
		flowVariant !== null && flowVariant !== undefined && flowVariant !== ''

		// In the missing-docs flow the same mobile session is reused across uploads.
		// The session status will still be REVIEW or CLOSED from the previous upload
		// cycle when this hook first mounts for the next document. Skip acting on
		// any terminal status until the mobile re-scans the QR and pushes the session
		// back to ACTIVE — that transition is the real signal for the new cycle.
		if (hasSpecialDocumentFlow && !hasSeenActiveRef.current) {
			if (session?.status === 'REVIEW' || session?.status === 'CLOSED') {
				console.log('[QR] Skipping stale terminal status on first poll (missing-docs reuse)', session?.status)
				return
			}
		}

		if (session?.status === 'PENDING' || session?.status === 'ACTIVE') {
			hasSeenActiveRef.current = true
		}

		if (
			session?.status === 'ACTIVE' &&
			activeStep !== StepsRichiesta.CONTATTI 
			&& !hasSpecialDocumentFlow //non va mai su pagina desktop
		) {
			window.location.href = `${import.meta.env.BASE_URL}/Home/MobileSwitch?mobile=true`
			//window.location.href = '/Home/MobileSwitch?mobile=true'
		}
			console.log("VARIANTTTT",flowVariant)

		if (session?.status === 'CLOSED') {
			clearInterval(intervalID ?? 0)
			switch (session.source) {
				case 'MOBILE':
					window.location.href = '/preventivatore/Home/MobileSwitch'
					break
				case 'DESKTOP':
					fetchUserData({})
					break
				default:
					break
			}
		} else if (session?.status === 'REVIEW') {
			if (hasSpecialDocumentFlow) {
				// MissingDocuments: user chose "continue on desktop" on mobile.
				// Documents are already saved; just close the modal and refresh status.
				clearInterval(intervalID ?? 0)
				setDocumentFlowVariant('')
				setQrMissingDocsCompleted(true)
				close()
				return
			}
			//modifica per chiudre la modale dopo che oi dati sono pronti
			pendingReviewRef.current = true
			fetchUserData({})
			return // NON close qui

			//chiude la modal
		} else if (session?.status === 'EXPIRED') {
			//alert('expired')
			/* dispatch({
				type: Actions.ShowModal,
				payload: 'Richiesta_EXPIRED',///@TODO ATTACCA MODAL NEW
			}) */
		}

		setMobile(
			session?.status === 'ACTIVE' ||
				(session?.status === 'CLOSED' &&
					activeStep === StepsRichiesta.RIEPILOGO) ||
				(session?.status === 'CLOSED' && session.source === 'DESKTOP')
		)
	}, [session])

	// On mount: reuse the existing session token if available, otherwise create a new one.
	useEffect(() => {
		if (state.mobileToken) {
			// Already have a registered session — start polling immediately with it
			const interval = window.setInterval(() => {
				fetchSession({ query: { idMobileSession: state.mobileToken } } as any)
			}, POLLING_INTERVAL * 1000)
			setIntervalID(interval)
		} else {
			// First open — request a new session from the BE
			fetchMobileToken({})
		}
		return () => clearInterval(intervalID ?? 0)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []) // run once on mount

	// When a fresh token arrives from initMobileSession, persist it and start polling
	useEffect(() => {
		if (mobileToken) {
			dispatch({
				type: Actions.UpdateMobileToken,
				payload: mobileToken,
			})

			if (userData === null) {
				const interval = window.setInterval(() => {
					fetchSession({ query: { idMobileSession: mobileToken } } as any)
				}, POLLING_INTERVAL * 1000)
				setIntervalID(interval)
			} else {
				clearInterval(intervalID ?? 0)
			}
		}

		return () => clearInterval(intervalID ?? 0)
	}, [mobileToken, userData])

	const [fpgesudo, sendFPGESUDO] = useApi({
		request: 'DbOperations/SendToFPgesudo',
		method: 'POST',
		sleeper: true,
		shouldCache: false,
	})

	useEffect(() => {
			
		/* avoid from being triggered if userData is not declared
		 * or if the user is at the thank you page */
		if (userData && activeStep === StepsRichiesta.CONTATTI ) {
			// STEP RICHIESTA
			dispatch({
				type: Actions.UpdateData,
				payload: {
					...state.data,
					Two: {
						...state.data.Two,
						Name: userData.Nome,
						Surname: userData.Cognome,
						FiscalCode: userData.CodiceFiscale,
						Gender: userData.Genere,
						BirthDate: userData.DataDiNascita,
						BirthLocation: userData.LuogoDiNascita,
					},
					Three: {
						...state.data.Three,
						Citizenship: userData.Cittadinanza,
						CurrentAddress: {
							...state.data.Three.CurrentAddress,
							CAP: calculateCAP(userData.CapRilascio),
						},
						Document: {
							...state.data.Three.Document,
							DocumentType: userData.TipoDocumento,
							DateOfIssue: getFormattedDate(userData.DataEmissione).day,
							Serial: userData.NumeroDocumento,
							Address: {
								...state.data.Three.Document.Address,
								Comune: userData.ComuneRilascio,
								Provincia: userData.ProvinciaRilascio ?? '',
							},
						},
					},
				},
			})

			if (session?.status === 'REVIEW') {
				if (pendingReviewRef.current) {
					pendingReviewRef.current = false

					setDocumentFlowVariant('mobileQr') //add per nasconfdere back button
					setActiveStep(StepsRichiesta.CONTATTI)

					setSelectedSubStep('contactData') // passa al componente successivo
					close() //  e ora puoi chiudere
					dispatch({ type: Actions.ShowModal, payload: 'DEFAULT' })
				}
			
				/* dispatch({
					type: Actions.ShowModal,
					payload: 'Richiesta_VERIFICA',
				}) */
			} else {
				//	window.location.href = '/Home/MobileSwitch' //questo è un BUG
			}
		} else if (userData) {
			// STEP THANK YOU
			sendFPGESUDO({
				body: {
					NumeroPratica: state.ocs.NumeroPratica.toString(),
					CodiceControparte: state.ocs.Controparte.toString(),
				},
			})
		}
	}, [userData])

	useEffect(() => {
		if (fpgesudo) {
			dispatch({
				type: Actions.SetThanksDone,
				payload: true,
			})
			close()
		}
	}, [fpgesudo])

	// Prefer the persisted token (reused across re-opens) over the freshly fetched one
	const effectiveToken = state.mobileToken ?? mobileToken

	return {
		data: {
			useOnMobile,
			mobileToken: effectiveToken,
			loadingToken: loadingToken && !state.mobileToken,
		},
	}
}

export { useQR }
